import { useQuery } from "@tanstack/react-query";

import { DEFAULT_PAGE_SIZE } from "@/domain/constants";
import { StaffFilterType, StaffRoleType } from "@/domain/staff";
import { useCurrentUserId, useCurrentUserType } from "@/stores/authStore";
import type { PaginationType } from "@/utils/helpers";

import { StaffApi } from "../api/staffApi";

export const StaffQueryKeys = {
  all: ["staff"] as const,
  me: () => [...StaffQueryKeys.all, "me"] as const,
  details: (id: string) => [...StaffQueryKeys.all, "details", id] as const,
  list: () => [...StaffQueryKeys.all, "list"],
  listTable: ({
    page = 1,
    pageSize = DEFAULT_PAGE_SIZE,
    sort,
    filters,
    needsPagination,
  }: PaginationType<StaffFilterType>) =>
    [
      ...StaffQueryKeys.list(),
      page,
      pageSize,
      sort,
      filters,
      needsPagination,
    ] as const,
  count: ({ filters }: { filters?: StaffFilterType }) =>
    [...StaffQueryKeys.all, "count", filters] as const,
  filterOptions: ({
    roles,
    campusesIds,
  }: {
    roles: StaffRoleType[];
    campusesIds: string[];
  }) => [...StaffQueryKeys.all, "filterOptions", roles, campusesIds] as const,
};

export const useCurrentStaffQuery = () => {
  const currentUserId = useCurrentUserId();
  const currentUserType = useCurrentUserType();

  const isStaffUser = currentUserType === "STAFF";

  return useQuery({
    queryKey: StaffQueryKeys.me(),
    queryFn: () => StaffApi.getStaff(currentUserId),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    enabled: Boolean(currentUserId && isStaffUser),
  });
};

export const useGetStaffQuery = (id: string) => {
  return useQuery({
    queryKey: StaffQueryKeys.details(id),
    queryFn: () => StaffApi.getStaff(id),
    enabled: Boolean(id),
  });
};

export const useListStaffQuery = ({
  page,
  pageSize = DEFAULT_PAGE_SIZE,
  sort,
  filters,
  needsPagination,
}: PaginationType<StaffFilterType>) => {
  return useQuery({
    queryKey: StaffQueryKeys.listTable({
      page,
      pageSize,
      sort,
      filters,
      needsPagination,
    }),
    queryFn: () =>
      StaffApi.listStaff({ page, pageSize, sort, filters, needsPagination }),
  });
};

export const useCountStaffQuery = ({
  filters,
}: { filters?: StaffFilterType } = {}) => {
  return useQuery({
    queryKey: StaffQueryKeys.count({ filters }),
    queryFn: () => StaffApi.countStaff({ filters }),
    initialData: 0,
  });
};

export const useStaffFilterOptions = ({
  roles,
  campusesIds,
  isEnabled = true,
}: {
  roles: StaffRoleType[];
  campusesIds: string[];
  isEnabled?: boolean;
}) => {
  return useQuery({
    queryKey: StaffQueryKeys.filterOptions({ roles, campusesIds }),
    queryFn: async () => {
      const staff = await StaffApi.listStaff({
        page: 1,
        needsPagination: false,
        filters: { roles, campusesIds },
      });
      return staff || [];
    },
    enabled: isEnabled,
    select: courses =>
      courses.map(staff => ({
        label: `${staff.first_name} ${staff.last_name}`,
        value: staff.id,
      })),
  });
};
